import { useChannel } from 'ably/react'
import React, { useEffect, useState } from 'react'
import { TFunction } from '@transifex/react'
import {
  getTerminalStatusChannelName,
  TerminalStatusProvider,
  useTerminalStatus,
} from '@lightspeed-payments/terminal-status'
import { IconWarningSolid20 } from '@lightspeed/unified-react-components'
import { selectActiveRegisterId } from '../../../state/slices/posState/posStateSlice'
import {
  selectTerminalStatusRefreshTrigger,
  setTerminalStatusLoadingState,
  setTerminalStatusWarningType,
  WarningType,
} from '../../../state/slices/terminalStatus/terminalStatusSlice'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { useRealtimeProvider } from '../../realtime-provider/RealtimeProvider'
import { useFetchPairing } from '../lspay-payment-modal/hooks/useFetchPairing'

const DEFAULT_DEVICE_MODEL = 'Terminal'
const WEAK_CONNECTION_THRESHOLD = 60
const LOW_BATTERY_THRESHOLD = 5

const TERMINAL_STATUS_IMAGES_PATH = 'payments/terminal-status'

export interface WarningConfig {
  icon: React.ReactNode
  title: string
  barMessage: string
  modalContent: {
    imagePath: string
    darkImagePath: string
    message: string
    bullets?: string[]
  }
}

export const WARNING_ICONS = {
  warning: <IconWarningSolid20 />,
} as const

const TerminalStatusEvents: React.FC = () => {
  const { client } = useRealtimeProvider()
  const dispatch = useAppDispatch()
  const refreshTrigger = useAppSelector(selectTerminalStatusRefreshTrigger)
  const {
    isInitialized,
    sendTerminalStatusRequest,
    terminalStatusData,
    isLoading,
  } = useTerminalStatus()
  const [initialRequestSent, setInitialRequestSent] = useState<boolean>(false)
  const terminalStatusEnabled = getFeatureFromCache(
    'lspay_terminal_status_enabled'
  )

  /**
   *  Initial Terminal Status Request on mount
   */
  useEffect(() => {
    const clearTerminalStatusEvents = () => {
      dispatch(setTerminalStatusWarningType(null))
      setInitialRequestSent(false)
    }

    // Clear terminal status if Ably connection is closed
    if (client?.connection.state === 'closed') {
      clearTerminalStatusEvents()
      return
    }

    if (!isInitialized || client?.connection.state !== 'connected') {
      return
    }

    sendTerminalStatusRequest()
    setInitialRequestSent(true)

    return () => {
      clearTerminalStatusEvents()
    }
  }, [
    isInitialized,
    client?.connection.state,
    sendTerminalStatusRequest,
    dispatch,
  ])

  /**
   * Listen for manual refresh requests via refreshTrigger changes
   */
  useEffect(() => {
    // Skip if initial request was not sent, or if this is the initial mount (refreshTrigger === 0)
    if (!initialRequestSent || refreshTrigger === 0) {
      return
    }

    sendTerminalStatusRequest()
    dispatch(setTerminalStatusLoadingState(null))
  }, [initialRequestSent, refreshTrigger, sendTerminalStatusRequest, dispatch])

  /**
   * Sync Warning Type to Redux store
   */
  useEffect(() => {
    if (!initialRequestSent || !terminalStatusEnabled) return

    dispatch(setTerminalStatusLoadingState(isLoading))

    const getWarningType = () => {
      if (isLoading) {
        return null
      }

      if (!terminalStatusData) {
        return 'offline'
      }

      if (terminalStatusData?.isBusy) {
        return 'busy'
      }

      const connectivityInfo = terminalStatusData?.connectivityInfo
      if (
        connectivityInfo?.strength &&
        connectivityInfo.strength < WEAK_CONNECTION_THRESHOLD
      ) {
        return 'weak_connection'
      }

      const batteryInfo = terminalStatusData?.batteryInfo
      if (batteryInfo && batteryInfo.level < LOW_BATTERY_THRESHOLD) {
        return 'low_battery'
      }

      // No warnings to show
      return null
    }

    const warningType = getWarningType()

    dispatch(setTerminalStatusWarningType(warningType))
  }, [
    initialRequestSent,
    dispatch,
    terminalStatusEnabled,
    isLoading,
    terminalStatusData,
  ])

  return null
}

export const TerminalStatusEventHandler: React.FC = () => {
  const { serialNumber } = useRealtimeProvider()
  const registerId = useAppSelector(selectActiveRegisterId)
  const { pairing } = useFetchPairing(registerId ?? '')

  const channelName = getTerminalStatusChannelName(serialNumber ?? '')
  const { channel } = useChannel(channelName)

  if (!serialNumber || !registerId || !channel) {
    return null
  }

  return (
    <TerminalStatusProvider
      channel={channel}
      options={{
        registerId,
        terminalInfo: {
          serialNumber,
          model: pairing?.deviceLabel ?? 'N/A',
          terminalId: pairing?.deviceID ?? 'N/A',
        },
      }}
    >
      <TerminalStatusEvents />
    </TerminalStatusProvider>
  )
}

export function getWarningConfig(
  t: TFunction,
  deviceModel?: string
): Record<WarningType, WarningConfig> {
  const model = deviceModel ?? DEFAULT_DEVICE_MODEL

  return {
    offline: {
      icon: WARNING_ICONS.warning,
      title: t('{deviceModel} is offline.', {
        deviceModel: model,
        _context: 'Terminal status offline title',
      }),
      barMessage: t(
        'Check that the terminal is powered on and connected to the internet.',
        {
          _context: 'Terminal status offline bar message',
        }
      ),
      modalContent: {
        imagePath: `${TERMINAL_STATUS_IMAGES_PATH}/offline-v1.svg`,
        darkImagePath: `${TERMINAL_STATUS_IMAGES_PATH}/offline-dark-v1.svg`,
        message: t(
          `{deviceModel} is offline. To fix this, check that the terminal is:`,
          {
            deviceModel: model,
            _context: 'Terminal status offline modal message',
          }
        ),
        bullets: [
          t('Powered on', {
            _context: 'Terminal status offline modal bullet 1',
          }),
          t('Connected to the internet', {
            _context: 'Terminal status offline modal bullet 2',
          }),
        ],
      },
    },
    busy: {
      icon: WARNING_ICONS.warning,
      title: t('{deviceModel} is busy.', {
        deviceModel: model,
        _context: 'Terminal status busy title',
      }),
      barMessage: t('Wait until it’s ready before starting a payment.', {
        _context: 'Terminal status busy bar message',
      }),
      modalContent: {
        imagePath: `${TERMINAL_STATUS_IMAGES_PATH}/busy-v1.svg`,
        darkImagePath: `${TERMINAL_STATUS_IMAGES_PATH}/busy-dark-v1.svg`,
        message: t(
          `{deviceModel} is starting up or processing another payment. To avoid payment interruption, wait until the terminal is ready.`,
          {
            deviceModel: model,
            _context: 'Terminal status busy modal message',
          }
        ),
      },
    },
    weak_connection: {
      icon: WARNING_ICONS.warning,
      title: t('{deviceModel} has a weak internet connection.', {
        deviceModel: model,
        _context: 'Terminal status weak internet connection title',
      }),
      barMessage: t('Move the terminal closer to your Wi-Fi router.', {
        _context: 'Terminal status weak internet connection bar message',
      }),
      modalContent: {
        imagePath: `${TERMINAL_STATUS_IMAGES_PATH}/weak_connection-v1.svg`,
        darkImagePath: `${TERMINAL_STATUS_IMAGES_PATH}/weak_connection-dark-v1.svg`,
        message: t(
          `{deviceModel} has a weak internet connection. To avoid payment interruption:`,
          {
            deviceModel: model,
            _context: 'Terminal status weak internet connection modal message',
          }
        ),
        bullets: [
          t('Move the terminal closer to your Wi-Fi router', {
            _context: 'Terminal status weak internet connection modal bullet 1',
          }),
          t('Connect to a different Wi-Fi network or use an ethernet cable', {
            _context: 'Terminal status weak internet connection modal bullet 2',
          }),
        ],
      },
    },
    low_battery: {
      icon: WARNING_ICONS.warning,
      title: t('{deviceModel} battery is low.', {
        deviceModel: model,
        _context: 'Terminal status low battery title',
      }),
      barMessage: t(
        'Charge the terminal now to prevent payment interruptions.',
        {
          _context: 'Terminal status low battery bar message',
        }
      ),
      modalContent: {
        imagePath: `${TERMINAL_STATUS_IMAGES_PATH}/low_battery-v1.svg`,
        darkImagePath: `${TERMINAL_STATUS_IMAGES_PATH}/low_battery-dark-v1.svg`,
        message: t(
          `{deviceModel} has less than {lowBatteryThreshold} battery remaining. To avoid payment interruption, charge the terminal now.`,
          {
            deviceModel: model,
            lowBatteryThreshold: new Intl.NumberFormat(undefined, {
              style: 'percent',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(LOW_BATTERY_THRESHOLD / 100),
            _context: 'Terminal status low battery modal message',
          }
        ),
      },
    },
  }
}
